import * as React from 'react';
import {
   CSSProperties,
   forwardRef,
   ReactElement,
   Ref,
   RefAttributes, useCallback, useEffect,
   useImperativeHandle, useMemo,
   useRef,
   useState
} from 'react';
import AntdAutoComplete from 'antd/lib/auto-complete';
import { convertSize } from '../../../utils/StyleUtils';
import {OptionsType} from '../select';
import {mapOptions, getFilterOptionHandler} from '../select/SelectUtils';

/**
 * {@docCategory AutoComplete}
 */
export interface AutoCompleteOption {
   value: string;
}

/**
 * {@docCategory AutoComplete}
 */
export interface AutoCompleteRefProps {
   focus: () => void;
   blur: () => void;
}

/**
 * {@docCategory AutoComplete}
 */
export interface AutoCompleteProps {

   /**
    * The component's optional id
    */
   id?: string;

   /**
    * Whether has border style
    * @defaultvalue true
    */
   bordered?: boolean;

   /**
    * Selected option
    */
   value?: string;

   /**
    * Initial selected option
    */
   defaultValue?: string;

   /**
    * The current input "search" text
    */
   searchValue?: string;

   /**
    * The placeholder of input
    */
   placeholder?: string;

   /**
    * Whether disabled select
    */
   disabled?: boolean;

   /**
    * Show clear button
    */
   allowClear?: boolean;

   /**
    * Sets the maximum number of characters that can be entered.
    */
   maxLength?: number;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Data in a form of strings or value-text objects.
    */
   options?: AutoCompleteOption[];

   /**
    *	Size of AutoComplete input
    *	@defaultvalue middle
    */
   size?: 'small' | 'middle' | 'large' | undefined;

   /**
    * Specify content to show when no result matches
    *
    * @defaultvalue Not Found
    */
   notFoundContent?: React.ReactNode;

   /**
    * Get focus by default
    * @defaultvalue false
    */
   autoFocus?: boolean;

   /**
    * If true, filter options by input, if function, filter options against it. The function will
    * receive two arguments, inputValue and option, if the function returns true, the option will
    * be included in the filtered set; Otherwise, it will be excluded
    */
   filterOption?: boolean | ((inputValue: string, option?: OptionsType[number]) => boolean);

   /**
    * Sort function for search options sorting, see Array.sort's compareFunction
    * @param optionA - The first option to compare
    * @param optionB - The second option to compare
    */
   filterSort?: (optionA: OptionsType[number], optionB: OptionsType[number]) => number;

   /**
    * Callback that is executed when select action is made on dropdown items.
    */
   onChange?: (value: string | undefined) => void;

   /**
    * Callback that is executed when search action is made via button click,
    * Enter key press or on each key press.
    */
   onSearch?: (searchText: string) => void;

   /**
    * Callback called when an option is selected
    * @param value - The value being selected
    * @param option - The option object
    */
   onSelect?: (value: string, option: OptionsType[number]) => void;

   /**
    * Called when entering the component
    */
   onFocus?: React.FocusEventHandler<HTMLElement>;

   /**
    * Called when leaving the component
    */
   onBlur?: React.FocusEventHandler<HTMLElement>;

   /**
    * Callback for key down event for input field
    */
   onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;
}

export const AutoComplete = forwardRef(function AutoComplete(props: AutoCompleteProps, ref: Ref<AutoCompleteRefProps>) {

   const antdAutoCompleteRef = useRef<any>(null);
   const [currentSearchString, setCurrentSearchString] = useState<string | undefined>(props.searchValue);

   const { minWidth, maxWidth, width, style : styleProp, options: optionsProp } = props;

   useImperativeHandle(ref, () => ({
      focus: () => {
         if (antdAutoCompleteRef && antdAutoCompleteRef.current) {
            antdAutoCompleteRef.current.focus();
         }
      },
      blur: () => {
         if (antdAutoCompleteRef && antdAutoCompleteRef.current) {
            antdAutoCompleteRef.current.blur();
         }
      }
   }));

   const optionFilterProp = 'value';
   const filterOptionHandler = useCallback(getFilterOptionHandler(optionFilterProp),[optionFilterProp]);

   const onSearchProp = props.onSearch;
   const onSearchHandler = useCallback((value: string) => {
      if (onSearchProp) {
         onSearchProp(value);
      }
      setCurrentSearchString(value);
   },[onSearchProp]);

   const onSelectProp = props.onSelect;
   const onSelectHandler = useCallback((value, option) => {
      if (onSelectProp) {
         onSelectProp(value, option);
      }
      setCurrentSearchString(undefined);
   },[onSelectProp]);

   const style = React.useMemo(()=>({
      width: convertSize(width, '250px'),
      minWidth: convertSize(minWidth, '60px'),
      maxWidth: convertSize(maxWidth),
      ...styleProp
   }),[minWidth, maxWidth, width, styleProp]);

   const optionLabel = 'label';
   const options = useMemo(() => {
      return (optionsProp && currentSearchString !== undefined && props.filterOption === undefined ?
              mapOptions(currentSearchString, optionsProp as OptionsType, optionLabel, true) : optionsProp);
   },[optionsProp, optionLabel, currentSearchString]);

   return (
      <AntdAutoComplete
         id={props.id}
         autoFocus={props.autoFocus}
         bordered={props.bordered}
         value={props.value}
         defaultValue={props.defaultValue}
         searchValue={props.searchValue}
         placeholder={props.placeholder}
         disabled={props.disabled}
         allowClear={props.allowClear}
         options={options}
         optionFilterProp={props.filterOption !== undefined ? undefined : optionFilterProp}
         filterOption={props.filterOption !== undefined ? props.filterOption : filterOptionHandler}
         filterSort={props.filterSort}
         size={props.size}
         maxLength={props.maxLength}
         notFoundContent={props.notFoundContent}
         onSearch={onSearchHandler}
         onSelect={onSelectHandler}
         onFocus={props.onFocus}
         onBlur={props.onBlur}
         onChange={props.onChange}
         onInputKeyDown={props.onInputKeyDown}
         style={style}
         className={props.className}
         ref={antdAutoCompleteRef}
      />
   );
}) as (props: AutoCompleteProps & RefAttributes<AutoCompleteRefProps>) => ReactElement | null;
